import { useEffect, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Navigate, useRouterState } from '@tanstack/react-router'

import { authKeys, useCurrentUserQuery } from '@/api/auth/auth-queries'
import { QueryErrorState } from '@/components/query-error-state'
import { Skeleton } from '@/components/ui/skeleton'
import { LOGIN_PATH } from '@/config/auth-routes'
import { onSessionExpired } from '@/features/auth/model/session-events'

interface RequireSessionProps {
  children: ReactNode
}

export function RequireSession({ children }: RequireSessionProps) {
  const queryClient = useQueryClient()
  const query = useCurrentUserQuery()
  const href = useRouterState({
    select: (state) => (state.resolvedLocation ?? state.location).href,
  })

  useEffect(
    () => onSessionExpired(() => queryClient.setQueryData(authKeys.me(), null)),
    [queryClient],
  )

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background p-6">
        <Skeleton className="h-40 w-full max-w-sm rounded-md" />
      </div>
    )
  }

  if (query.isError) {
    return (
      <div className="flex h-full items-center justify-center bg-background p-6">
        <QueryErrorState
          message="Couldn’t reach the server."
          onRetry={() => query.refetch()}
        />
      </div>
    )
  }

  if (query.data === null) {
    return <Navigate to={LOGIN_PATH} search={{ redirect: href }} replace />
  }

  return <>{children}</>
}
